/** @format */

import React from 'react'
import styled from 'styled-components'
import { _$, Skeleton } from '@king-design/react'
import { useAsync } from 'react-use'
import { getOverviews } from 'src/api/ApiAIObservability'
import { formatValueOverThousand } from 'src/utils/common'
import variable from 'src/static/styles/var'

interface OverviewProps {
  className?: string
  serviceName?: string
  startTime: number
  endTime: number
  region?: string
}

interface MetricCardProps {
  label: string
  value: React.ReactNode
  hasError?: boolean
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, hasError }) => (
  <article className="metric-card" role="listitem">
    <div className="metric-card__header">
      <span className="metric-card__label">{label}</span>
    </div>
    <span className={`metric-card__value${hasError ? ' metric-card__value--danger' : ''}`}>
      {value}
    </span>
  </article>
)

const Overview: React.FC<OverviewProps> = ({
  className,
  serviceName,
  startTime,
  endTime,
  region
}) => {
  const { value: overviewData, loading } = useAsync(async () => {
    if (startTime && endTime) {
      const result = await getOverviews({
        ServiceName: serviceName || '',
        StartTime: startTime,
        EndTime: endTime,
        Region: region
      })
      return result
    }
    return {}
  }, [serviceName, startTime, endTime, region])

  const errorCount = Number(overviewData?.ErrorCount)
  const hasErrorCount = !Number.isNaN(errorCount) && errorCount > 0

  return (
    <div className={className}>
      <section aria-label={_$('总览')}>
        {loading ? (
          <Skeleton loading={true} />
        ) : (
          <div className="overview-metrics__grid" role="list">
            {!serviceName && overviewData?.ServiceCount !== undefined ? (
              <MetricCard label={_$('服务数')} value={overviewData?.ServiceCount ?? '-'} />
            ) : null}
            <MetricCard label={_$('会话数')} value={overviewData?.ActiveSessionCount ?? '-'} />
            <MetricCard
              label={_$('Token消耗数')}
              value={
                <>
                  {formatValueOverThousand(overviewData?.TokenTotalUsage) ?? '-'}
                  {overviewData?.TokenTotalUsage > 10000 && (
                    <span className="metric-card__suffix">k</span>
                  )}
                </>
              }
            />
            <MetricCard label={_$('调用数')} value={overviewData?.InvocationCount ?? '-'} />
            <MetricCard
              label={_$('错误数')}
              value={overviewData?.ErrorCount ?? '-'}
              hasError={hasErrorCount}
            />
            <MetricCard
              label={_$('错误率')}
              value={
                <>
                  {overviewData?.ErrorRate ?? '-'}
                  <span className="metric-card__suffix">%</span>
                </>
              }
            />
          </div>
        )}
      </section>
    </div>
  )
}

export default styled(Overview)`
  .overview-metrics__grid {
    margin-top: 16px;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;

    .metric-card {
      display: flex;
      flex-direction: column;
      padding: 16px;
      border-radius: 8px;
      background: #ffffff;
      border: 1px solid #f0f0f0;
      transition: box-shadow 0.2s ease;

      &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
      }

      .metric-card__header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;

        .metric-card__icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          background: transparent;
        }

        .metric-card__label {
          font-size: 13px;
          font-weight: 400;
          line-height: 20px;
          color: rgba(0, 0, 0, 0.45);
        }
      }

      .metric-card__value {
        font-size: 28px;
        font-weight: 600;
        line-height: 36px;
        color: rgba(0, 0, 0, 0.88);
        font-variant-numeric: tabular-nums;
        padding-left: 4px;

        &.metric-card__value--danger {
          color: ${variable.errorColor};
        }

        .metric-card__suffix {
          margin-left: 2px;
          font-size: 16px;
          font-weight: 500;
          line-height: 1;
          color: rgba(0, 0, 0, 0.45);
        }
      }
    }
  }
`
